/**
 * Unified Review pane — single "what needs my attention?" surface
 * replacing the SUMMARY block, REVIEW QUEUE, ValidationStatusBar,
 * ValidationSummaryDrawer, in-table unmapped banner, and toolbar
 * Decisions button.
 *
 * Tabs (only non-empty are visible, except All):
 *   All · Errors · Warnings · Decisions · Unmapped · Broken · Stale
 *
 * Filter chips compose with the active tab. Click on an item selects
 * the field via `onSelect`, which the parent threads into
 * `useFieldSelection` so the canvas/table view focuses the row and
 * the right Inspector re-scopes.
 *
 * Inputs are pre-computed by `useReviewItems` — this component does no
 * derivation, only filtering + rendering. Counts shown in tab badges
 * are from the same hook so they cannot drift from per-row chips.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import type { ReviewItem, ReviewItemBucket } from '../hooks/useReviewItems';
import type { DecisionKind } from '../../services/mappingApi';
import type { ReviewItem as _RI } from '../hooks/useReviewItems';
import { ReviewItemRow } from './ReviewItemRow';
import {
  EMPTY_FILTERS,
  ReviewFilters,
  type ReviewFilterState,
} from './ReviewFilters';
import { confidenceBucket } from '../util/colors';
import { fieldKey, type FieldSelection } from '../state/useFieldSelection';
import { useNotifications } from '../../context/NotificationContext';
import { useResolveAutoRefresh } from '../../components/workspace/readiness/useResolveAutoRefresh';
import {
  fetchReadiness,
  resolvePostMappingFindings,
  uploadLookupFile,
} from '../../services/readinessApi';
import { useReadinessDeepLink } from '../../components/workspace/readiness/useReadinessDeepLink';
import ReadinessFindingCard from '../../components/workspace/readiness/ReadinessFindingCard';
import type {
  ReadinessFinding,
  ReadinessResolutionRequest,
  ReadinessStatus,
} from '../../types/readiness';

export type ReviewBucketTab = 'all' | ReviewItemBucket;

export interface ReviewPaneProps {
  items: ReviewItem[];
  /** Counts per bucket (used for tab badges + which tabs to show). */
  counts: Record<ReviewItemBucket, number> & { all: number };
  /** Currently-selected field, used to highlight the matching row. */
  activeSelection: FieldSelection;
  onSelect: (item: ReviewItem) => void;
  /** Optional initial tab (e.g., from drift banner click). */
  initialTab?: ReviewBucketTab;
  /** Free-text search from canvas search bar — narrows by table/field/message substring. */
  searchQuery?: string;
  /**
   * When the canvas is in detail mode (zoomed into one table), the
   * editor passes that table here so the Review pane scopes by it.
   */
  tableFilter?: string | null;
  /**
   * Optional: clear the implicit `tableFilter` (e.g. exit canvas
   * detail mode). When provided, the "Filtered to TABLENAME" banner
   * shows a `clear` link.
   */
  onClearTableFilter?: () => void;
  /**
   * Plan §"Tab-count preload": fallback decision-kind counts read from
   * `mappingResponse.metadata.validator_summary.by_kind`. Used when
   * `pendingDecisionsQuery` hasn't resolved yet so the chip badges
   * paint immediately.
   */
  decisionKindFallbackCounts?: Partial<Record<DecisionKind, number>>;
  /** Plan §J: Esc clears the active selection (caller decides what that means). */
  onClearSelection?: () => void;
  /** Plan §I: render an error state when the decisions query failed. */
  decisionsError?: boolean;
  /** Readiness rollout — when provided, the pane fetches structured
   *  readiness findings from /readiness and renders a banner above
   *  the existing review list summarizing root-cause grouped state.
   *  Optional so legacy callers (without sessionId) keep working. */
  sessionId?: string;
}

const TAB_DEFINITIONS: Array<{ value: ReviewBucketTab; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'error', label: 'Errors' },
  { value: 'warning', label: 'Warnings' },
  { value: 'decision', label: 'Decisions' },
  { value: 'unmapped', label: 'Unmapped' },
  { value: 'broken', label: 'Broken' },
  { value: 'stale', label: 'Stale' },
];

function matchTab(item: ReviewItem, tab: ReviewBucketTab): boolean {
  if (tab === 'all') return true;
  if (tab === 'error') return item.severity === 'error' || item.bucket === 'error';
  if (tab === 'warning') return item.bucket === 'warning';
  return item.bucket === tab;
}

function matchFilters(item: ReviewItem, f: ReviewFilterState): boolean {
  if (f.confidence) {
    const b = confidenceBucket(item.confidence);
    if (b !== f.confidence) return false;
  }
  if (f.provenance) {
    const aiBuckets: Array<NonNullable<ReviewItem['field_status']>> = [
      'ai_generated',
      'inherited',
      'broken',
      'stale',
    ];
    const manualBuckets: Array<NonNullable<ReviewItem['field_status']>> = [
      'approved',
      // user_authored is not yet in the FieldStatus union but the chip work
      // adds it in this issue — fall back to checking string equality.
    ];
    const fs = item.field_status ?? '';
    if (item.bucket === 'unmapped') return false;
    if (f.provenance === 'ai') {
      if (!aiBuckets.includes(fs as any) && fs !== '') return false;
      // Items without field_status (e.g., schema-rule violations) keep
      // an AI-leaning default — they came from the LLM pipeline.
      if (fs === '') return true;
    } else {
      if (!manualBuckets.includes(fs as any) && fs !== ('user_authored' as any)) return false;
    }
  }
  if (f.decisionKind) {
    if (item.decision_kind !== f.decisionKind) return false;
  }
  return true;
}

function matchSearch(item: ReviewItem, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (item.target_table.toLowerCase().includes(needle)) return true;
  if (item.target_field?.toLowerCase().includes(needle)) return true;
  if (item.message.toLowerCase().includes(needle)) return true;
  if (item.source_field?.toLowerCase().includes(needle)) return true;
  return false;
}

export const ReviewPane: React.FC<ReviewPaneProps> = ({
  items,
  counts,
  activeSelection,
  onSelect,
  initialTab,
  searchQuery,
  tableFilter,
  onClearTableFilter,
  decisionKindFallbackCounts,
  onClearSelection,
  decisionsError = false,
  sessionId,
}) => {
  const { showToast } = useNotifications();
  // Readiness rollout — fetch structured findings + status. The pane
  // continues to render the legacy ReviewItem list below; this banner
  // surfaces the unified readiness view at the top so the user sees
  // the consolidated answer first. Refreshes on window focus + when
  // the active selection changes (cheap signal that the mapping was
  // edited). Failure is silent — the banner just doesn't render.
  const [readinessFindings, setReadinessFindings] = useState<
    ReadinessFinding[]
  >([]);
  const [readinessStatus, setReadinessStatus] =
    useState<ReadinessStatus | null>(null);
  const refreshReadiness = React.useCallback(() => {
    if (!sessionId) return () => {};
    let cancelled = false;
    fetchReadiness({ sessionId, consumerKind: 'mapping_canvas' })
      .then((resp) => {
        if (cancelled) return;
        setReadinessFindings(resp.findings || []);
        setReadinessStatus(resp.status);
      })
      .catch(() => {
        if (cancelled) return;
        setReadinessFindings([]);
        setReadinessStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);
  React.useEffect(() => {
    return refreshReadiness();
  }, [refreshReadiness, activeSelection]);
  // SSE-driven refresh: when the orchestrator finishes a bg dispatch
  // for this session, the rebroadcast event triggers refreshReadiness.
  useResolveAutoRefresh(sessionId, refreshReadiness);
  React.useEffect(() => {
    const onFocus = () => refreshReadiness();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshReadiness();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshReadiness]);

  // Plan §"Findings group by root cause" — collapse multi-field
  // findings sharing the same source identity into a single card with
  // an "Affects N fields" count. Keys on (code, source_table,
  // source_column) which is what the aggregation_key helper on the
  // Python side uses too.
  const aggregatedFindings = useMemo(() => {
    const groups = new Map<
      string,
      { sample: ReadinessFinding; affects: Set<string>; count: number }
    >();
    for (const f of readinessFindings) {
      const srcTable = (f.observed as any)?.source_table || '';
      const srcCol = (f.observed as any)?.source_column || '';
      const key = `${f.code}|${srcTable}|${srcCol}`;
      const fieldKey = `${f.target_path}`;
      const existing = groups.get(key);
      if (existing) {
        existing.affects.add(fieldKey);
        existing.count += 1;
      } else {
        groups.set(key, {
          sample: f,
          affects: new Set([fieldKey]),
          count: 1,
        });
      }
    }
    return Array.from(groups.values());
  }, [readinessFindings]);

  // Smart default: when the operator opens the canvas with hundreds of
  // items, dropping them on `All` is overwhelming. Land on the most
  // actionable non-empty bucket so the first thing they see is what
  // they can do, not what's wrong.
  const smartDefaultTab = useMemo<ReviewBucketTab>(() => {
    if (initialTab) return initialTab;
    if (counts.error > 0) return 'error';
    if (counts.decision > 0) return 'decision';
    if (counts.unmapped > 0) return 'unmapped';
    if (counts.broken > 0) return 'broken';
    if (counts.stale > 0) return 'stale';
    return 'all';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tab, setTab] = useState<ReviewBucketTab>(smartDefaultTab);
  const [filters, setFilters] = useState<ReviewFilterState>(EMPTY_FILTERS);

  // When the parent dictates a new initialTab (e.g., drift banner
  // click activates Broken), respect it.
  useEffect(() => {
    if (initialTab && initialTab !== tab) setTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  const tableFilteredItems = useMemo(
    () => (tableFilter ? items.filter((it) => it.target_table === tableFilter) : items),
    [items, tableFilter],
  );

  const tabFiltered = useMemo(
    () => tableFilteredItems.filter((it) => matchTab(it, tab)),
    [tableFilteredItems, tab],
  );

  const fullyFiltered = useMemo(
    () =>
      tabFiltered
        .filter((it) => matchFilters(it, filters))
        .filter((it) => matchSearch(it, searchQuery ?? '')),
    [tabFiltered, filters, searchQuery],
  );

  // Decision-kind counts within the current tab (for chip badges).
  // Falls back to `validator_summary.by_kind` when no decisions are
  // in scope yet (initial render before `pendingDecisionsQuery`
  // resolves) so badges paint immediately.
  const decisionKindCounts = useMemo(() => {
    const out: Partial<Record<DecisionKind, number>> = {};
    for (const it of tabFiltered) {
      if (it.decision_kind) out[it.decision_kind] = (out[it.decision_kind] ?? 0) + 1;
    }
    if (Object.keys(out).length === 0 && decisionKindFallbackCounts) {
      return decisionKindFallbackCounts;
    }
    return out;
  }, [tabFiltered, decisionKindFallbackCounts]);

  // Decision-kind chips are only relevant when the operator is
  // explicitly on the Decisions tab. Showing them on `All` adds 4
  // chips to a 320px row and pushes everything else into a wrap.
  const showDecisionKindChips = useMemo(
    () => tab === 'decision',
    [tab],
  );

  // Visible tabs: All always; others only when count > 0.
  const visibleTabs = TAB_DEFINITIONS.filter((td) => {
    if (td.value === 'all') return true;
    if (td.value === 'error') return counts.error > 0;
    if (td.value === 'warning') return counts.warning > 0;
    if (td.value === 'decision') return counts.decision > 0;
    if (td.value === 'unmapped') return counts.unmapped > 0;
    if (td.value === 'broken') return counts.broken > 0;
    if (td.value === 'stale') return counts.stale > 0;
    return false;
  });

  // If the active tab disappears (e.g., user resolved last decision
  // and Decisions tab is hidden), fall back to All.
  useEffect(() => {
    if (!visibleTabs.find((t) => t.value === tab)) setTab('all');
  }, [visibleTabs, tab]);

  // Deep-link router — clicking a Readiness banner card navigates the
  // user to the offending field on the mapping canvas (plan §"One
  // button per issue"). The hook also lives in AssemblyInspectorPanel;
  // sharing it means click behavior is identical across surfaces.
  const { handlePrimaryAction } = useReadinessDeepLink();

  const activeKey = fieldKey(activeSelection);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState(400);

  // Scroll active item into view on selection change.
  useEffect(() => {
    if (!activeKey || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-item-key="${CSS.escape(activeKey)}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeKey]);

  // Measure list container height — react-window needs a fixed pixel
  // height. Re-measures on resize.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current;
    const update = () => setListHeight(el.clientHeight || 400);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Plan §H: virtualize when the list grows large. Threshold chosen
  // to keep the simple map-render path for the common case
  // (operators with under ~80 review items see no virtualization
  // jitter on fast scroll).
  const VIRTUALIZE_THRESHOLD = 80;
  const ROW_HEIGHT = 52;
  const shouldVirtualize = fullyFiltered.length >= VIRTUALIZE_THRESHOLD;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Plan §"One findings surface" + §"Findings group by root cause"
          — full ReviewPane rebuild on ReadinessFinding shape. When the
          Readiness endpoint returns findings, render them as proper
          ReadinessFindingCard components (same shape used in
          AssemblyInspectorPanel) with severity icons, plain-English
          titles + details, expandable evidence, and a primary "Fix in
          mapping →" action that fires the deep-link router. Each
          aggregated group renders one card with an "Affects N fields"
          subtitle from the root-cause aggregator. The legacy
          ReviewItem list below remains as the secondary view for
          decisions / broken-ref / stale items that don't have a
          ReadinessFinding equivalent yet. */}
      {readinessStatus && aggregatedFindings.length > 0 && (
        <Box
          sx={{
            p: 1,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            bgcolor:
              readinessStatus === 'not_ready'
                ? 'rgba(220, 38, 38, 0.04)'
                : readinessStatus === 'ready_with_notes'
                  ? 'rgba(245, 158, 11, 0.04)'
                  : 'transparent',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              display: 'block',
              mb: 1,
              color:
                readinessStatus === 'not_ready'
                  ? 'error.main'
                  : readinessStatus === 'ready_with_notes'
                    ? 'warning.dark'
                    : 'success.main',
            }}
          >
            {readinessStatus === 'not_ready'
              ? `Not ready — ${aggregatedFindings.filter((g) => g.sample.severity === 'blocker').length} thing${aggregatedFindings.filter((g) => g.sample.severity === 'blocker').length === 1 ? '' : 's'} to fix`
              : readinessStatus === 'ready_with_notes'
                ? `Ready — ${aggregatedFindings.length} note${aggregatedFindings.length === 1 ? '' : 's'}`
                : 'Ready'}
          </Typography>
          <Stack
            spacing={0.75}
            sx={{
              maxHeight: '60vh',
              overflowY: 'auto',
              pr: 0.5,
            }}
          >
            {aggregatedFindings.map((g) => {
              // Decorate the aggregated finding with the "affects N
              // fields" annotation directly in user_detail so the
              // ReadinessFindingCard renders consistently with the
              // assembly node's card without needing extra props.
              const decoratedDetail = g.affects.size > 1
                ? `${g.sample.user_detail} (Affects ${g.affects.size} fields.)`
                : g.sample.user_detail;
              const decoratedFinding: ReadinessFinding = {
                ...g.sample,
                user_detail: decoratedDetail,
              };
              return (
                <ReadinessFindingCard
                  key={`${g.sample.code}-${g.sample.target_path}`}
                  finding={decoratedFinding}
                  onPrimaryAction={handlePrimaryAction}
                  onResolve={
                    sessionId
                      ? async (req: ReadinessResolutionRequest) => {
                          const resp = await resolvePostMappingFindings(
                            sessionId,
                            [req],
                          );
                          if (resp.errors && resp.errors.length > 0) {
                            const first = resp.errors[0];
                            throw new Error(
                              first?.reason || 'Could not apply your choice.',
                            );
                          }
                          showToast(
                            'Saved',
                            resp.next_step || 'Your choice has been recorded.',
                            'success',
                          );
                          refreshReadiness();
                          // SSE rebroadcast drives subsequent refresh
                          // when the bg dispatch lands — see the
                          // ``useResolveAutoRefresh`` hook below.
                          return {
                            dispatched: resp.dispatched || {},
                            next_step: resp.next_step,
                          };
                        }
                      : undefined
                  }
                  onUploadLookup={
                    sessionId
                      ? async (file: File) => {
                          const r = await uploadLookupFile(sessionId, file);
                          return { alias: r.table_name };
                        }
                      : undefined
                  }
                />
              );
            })}
          </Stack>
        </Box>
      )}
      {/* Plan §"One findings surface" — when Readiness is active,
          the legacy "Review (N)" header + Errors/Warnings/Decisions/
          Unmapped tabs + flat ReviewItem list double-count the same
          underlying issues that the Readiness banner already groups
          by root cause. Render them only when Readiness has nothing
          to show (sessions without a sessionId prop, or fully-ready
          mappings where the legacy view still has historical
          decisions). */}
      {!(readinessStatus && aggregatedFindings.length > 0) && (
        <React.Fragment>
      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Typography variant="subtitle2">Review ({counts.all})</Typography>
        {tableFilter && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              mt: 0.25,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Filtered to <strong>{tableFilter}</strong>
            </Typography>
            {onClearTableFilter && (
              <Typography
                component="button"
                variant="caption"
                onClick={onClearTableFilter}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: 'primary.main',
                  cursor: 'pointer',
                  p: 0,
                  '&:hover': { textDecoration: 'underline' },
                }}
                aria-label="Clear table filter"
              >
                clear
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as ReviewBucketTab)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          minHeight: 30,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          '& .MuiTab-root': {
            minHeight: 30,
            minWidth: 0,
            py: 0,
            px: 0.75,
            fontSize: 11,
            textTransform: 'none',
          },
        }}
      >
        {visibleTabs.map((td) => {
          const c =
            td.value === 'all' ? counts.all : (counts as any)[td.value] ?? 0;
          return (
            <Tab
              key={td.value}
              value={td.value}
              label={`${td.label} ${c}`}
            />
          );
        })}
      </Tabs>
      <ReviewFilters
        state={filters}
        onChange={setFilters}
        showDecisionKindChips={showDecisionKindChips}
        decisionKindCounts={decisionKindCounts}
      />
      <Box
        ref={listRef}
        role="listbox"
        aria-label="Review items"
        tabIndex={-1}
        onKeyDown={(e) => {
          if (!listRef.current) return;
          // Arrow nav between focused review-item rows.
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const rows = Array.from(
              listRef.current.querySelectorAll<HTMLElement>('[role="button"][data-row-id]'),
            );
            if (rows.length === 0) return;
            const active = document.activeElement as HTMLElement | null;
            const idx = active ? rows.indexOf(active) : -1;
            const next =
              e.key === 'ArrowDown'
                ? rows[Math.min(rows.length - 1, idx + 1)]
                : rows[Math.max(0, idx - 1)];
            next?.focus();
            e.preventDefault();
          }
          // Plan §J: Esc clears the active selection.
          if (e.key === 'Escape' && onClearSelection) {
            onClearSelection();
            e.preventDefault();
          }
        }}
        sx={{ flex: 1, overflowY: 'auto' }}
      >
        {tab === 'decision' && decisionsError && (
          <Box sx={{ p: 1.5 }}>
            <Typography variant="body2" color="error.main" gutterBottom>
              Couldn't load pending decisions.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Other tabs still work. Try refreshing the canvas to retry.
            </Typography>
          </Box>
        )}
        {fullyFiltered.length === 0 ? (
          <Stack spacing={1} sx={{ p: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No items match.
            </Typography>
            {(filters.confidence || filters.provenance || filters.decisionKind) && (
              <Button size="small" variant="text" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            )}
          </Stack>
        ) : shouldVirtualize ? (
          <FixedSizeList
            height={listHeight}
            itemCount={fullyFiltered.length}
            itemSize={ROW_HEIGHT}
            width="100%"
          >
            {({ index, style }) => {
              const it = fullyFiltered[index];
              const itemKey = fieldKey({
                scope: 'field',
                target_table: it.target_table,
                target_field: it.target_field ?? '',
                hierarchy_path: it.hierarchy_path,
                source: 'review-pane',
              } as any);
              return (
                <Box key={it.id} data-item-key={itemKey} style={style}>
                  <ReviewItemRow
                    item={it}
                    isActive={
                      activeKey !== '' &&
                      it.target_table === (activeSelection?.target_table ?? '') &&
                      ((activeSelection?.scope === 'field' &&
                        it.target_field === (activeSelection as any).target_field) ||
                        (activeSelection?.scope === 'table' && !it.target_field))
                    }
                    onSelect={onSelect}
                  />
                </Box>
              );
            }}
          </FixedSizeList>
        ) : (
          fullyFiltered.map((it) => (
            <Box key={it.id} data-item-key={fieldKey({
              scope: 'field',
              target_table: it.target_table,
              target_field: it.target_field ?? '',
              hierarchy_path: it.hierarchy_path,
              source: 'review-pane',
            } as any)}>
              <ReviewItemRow
                item={it}
                isActive={
                  activeKey !== '' &&
                  it.target_table === (activeSelection?.target_table ?? '') &&
                  ((activeSelection?.scope === 'field' &&
                    it.target_field === (activeSelection as any).target_field) ||
                    (activeSelection?.scope === 'table' && !it.target_field))
                }
                onSelect={onSelect}
              />
            </Box>
          ))
        )}
      </Box>
        </React.Fragment>
      )}
    </Box>
  );
};

export default ReviewPane;
